import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Loading } from '@/components/Loading';
import { cn } from '@/lib/utils';
import { llmChatApi } from '@/lib/api/llm-chat';
import { AgentConfigDto, AgentType } from '@paladin-monorepo/common';
import { agentConfigRequiresInquiryBinding } from '@/lib/llm-chat-selectable-inquiries';
import { pickDefaultAgentConfig, agentDefaultScopeFromPickerMode } from '@/lib/pick-default-agent-config';

export type SelectLlmAgentMode = 'inquirer' | 'supplier';

export interface SelectLlmAgentTypePanelProps {
  mode: SelectLlmAgentMode;
  /** When set (e.g. from URL `?agent=`), pre-selects that agent if allowed for the mode. */
  initialAgentQuery: string | null;
  /** Supplier without inquiry scope may need to pick an inquiry for some agents. */
  unscopedSupplierFlow?: boolean;
  hasSelectableInquiries?: boolean;
  inquiriesLoading?: boolean;
  onConfirm: (config: AgentConfigDto) => void | Promise<void>;
  onCancel: () => void;
}

function isAgentConfigActionable(
  config: AgentConfigDto,
  unscopedSupplierFlow: boolean,
  hasSelectableInquiries: boolean,
  inquiriesLoading: boolean,
): boolean {
  if (!unscopedSupplierFlow || !agentConfigRequiresInquiryBinding(config)) {
    return true;
  }
  if (inquiriesLoading) return false;
  return hasSelectableInquiries;
}

function isAgentConfigInquiryBlocked(
  config: AgentConfigDto,
  unscopedSupplierFlow: boolean,
  hasSelectableInquiries: boolean,
  inquiriesLoading: boolean,
): boolean {
  return (
    unscopedSupplierFlow &&
    agentConfigRequiresInquiryBinding(config) &&
    !inquiriesLoading &&
    !hasSelectableInquiries
  );
}

export function SelectLlmAgentTypePanel({
  mode,
  initialAgentQuery,
  unscopedSupplierFlow = false,
  hasSelectableInquiries = true,
  inquiriesLoading = false,
  onConfirm,
  onCancel,
}: SelectLlmAgentTypePanelProps) {
  const { t } = useTranslation();
  const { data: configs, isLoading: configsLoading } = useQuery({
    queryKey: ['agent-configs'],
    queryFn: () => llmChatApi.getAgentConfigs(),
  });

  const allowed = useMemo(() => configs ?? [], [configs]);

  const anyConfigRequiresInquiry = useMemo(
    () => allowed.some(agentConfigRequiresInquiryBinding),
    [allowed],
  );

  const [selected, setSelected] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedConfig = useMemo(
    () => allowed.find((c) => c.agentType === selected) ?? null,
    [allowed, selected],
  );

  const selectedRequiresInquiry =
    !!selectedConfig && agentConfigRequiresInquiryBinding(selectedConfig);

  const selectedActionable = selectedConfig
    ? isAgentConfigActionable(
        selectedConfig,
        unscopedSupplierFlow,
        hasSelectableInquiries,
        inquiriesLoading,
      )
    : false;

  const inquiryGateLoading =
    unscopedSupplierFlow && anyConfigRequiresInquiry && inquiriesLoading;

  useEffect(() => {
    const actionable = allowed.filter((c) =>
      isAgentConfigActionable(c, unscopedSupplierFlow, hasSelectableInquiries, inquiriesLoading),
    );
    if (actionable.length === 0) {
      setSelected(null);
      return;
    }
    const fromQuery =
      initialAgentQuery &&
      actionable.some((c) => c.agentType === initialAgentQuery)
        ? initialAgentQuery
        : null;
    const fallbackType = mode === 'supplier' ? AgentType.Generic : AgentType.InquirerGuide;
    const scope = agentDefaultScopeFromPickerMode(mode);
    const defaultConfig = pickDefaultAgentConfig(actionable, fallbackType, scope);
    const pick = fromQuery ?? defaultConfig?.agentType ?? actionable[0]?.agentType ?? null;
    setSelected(pick);
  }, [initialAgentQuery, allowed, unscopedSupplierFlow, hasSelectableInquiries, inquiriesLoading, mode]);

  const handleConfirm = async () => {
    if (!selectedConfig || !selectedActionable) return;
    setIsSubmitting(true);
    try {
      await onConfirm(selectedConfig);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (configsLoading || inquiryGateLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Loading />
      </div>
    );
  }

  const confirmDisabled =
    !selected || isSubmitting || allowed.length === 0 || !selectedActionable;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-6 overflow-y-auto px-6 py-8">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('llmChat.newSessionTitle')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('llmChat.newSessionDescription')}</p>
        </div>

        {allowed.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('monomoAi.empty.predefined')}</p>
        ) : (
          <div
            className={cn(
              'grid w-full gap-3',
              allowed.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 max-w-md mx-auto',
            )}
          >
            {allowed.map((config) => {
              const actionable = isAgentConfigActionable(
                config,
                unscopedSupplierFlow,
                hasSelectableInquiries,
                inquiriesLoading,
              );
              const inquiryBlocked = isAgentConfigInquiryBlocked(
                config,
                unscopedSupplierFlow,
                hasSelectableInquiries,
                inquiriesLoading,
              );

              return (
                <button
                  key={config.id}
                  type="button"
                  onClick={() => actionable && setSelected(config.agentType)}
                  disabled={!actionable}
                  className={cn(
                    'flex min-h-[7.5rem] flex-col justify-center gap-1 rounded-lg border p-4 text-left transition-colors',
                    !actionable && 'cursor-not-allowed opacity-60',
                    actionable && 'hover:bg-accent',
                    actionable &&
                      selected === config.agentType &&
                      'border-primary bg-primary/5',
                  )}
                >
                  <span className="font-medium">
                    {t(`llmChat.agentTypes.${config.agentType}.label`, { defaultValue: config.displayName })}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t(`llmChat.agentTypes.${config.agentType}.description`, { defaultValue: config.description })}
                  </span>
                  {inquiryBlocked && (
                    <p className="mt-2 text-xs leading-snug text-amber-700 dark:text-brand-primary">
                      {t('llmChat.noSelectableInquiryForAgent')}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-auto flex flex-wrap items-center justify-end gap-2 border-t pt-6">
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            {t('llmChat.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={confirmDisabled}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'supplier' && selectedRequiresInquiry
              ? t('llmChat.continueToInquiry')
              : t('llmChat.startSession')}
          </Button>
        </div>
      </div>
    </div>
  );
}
